import NProgress from "nprogress";

const doc = document;
const START_DELAY_MS = 140;
const PROGRESS_ID_KEY = "__wmuProgressId";
const PROGRESS_SKIP_ATTR = "data-network-progress";

let pendingCount = 0;
let visible = false;
let startTimer = 0;
let fallbackSequence = 0;
const trackedRequests = new Set<string>();
let bootedListeners = false;

export type HTMXProgressDetail = {
  elt?: EventTarget | null;
  target?: EventTarget | null;
  xhr?: XMLHttpRequest | null;
  requestConfig?: Record<string, unknown> | null;
};

type ProgressCarrier = HTMXProgressDetail & Record<string, unknown>;

export type WMUNetworkProgressAPI = {
  boot: () => void;
  start: () => void;
  done: () => void;
  track: <T>(promise: Promise<T>) => Promise<T>;
  shouldTrackHTMXRequest: (detail: unknown) => boolean;
};

function setProgressID(carrier: unknown, id: string): void {
  if (carrier && typeof carrier === "object") {
    Object.defineProperty(carrier, PROGRESS_ID_KEY, {
      configurable: true,
      enumerable: false,
      writable: true,
      value: id,
    });
  }
}

function getProgressID(carrier: unknown): string {
  if (!carrier || typeof carrier !== "object") {
    return "";
  }
  const raw = (carrier as Record<string, unknown>)[PROGRESS_ID_KEY];
  return typeof raw === "string" ? raw : "";
}

function requestSource(detail: HTMXProgressDetail | null): HTMLElement | null {
  if (!detail) {
    return null;
  }
  if (detail.elt instanceof HTMLElement) {
    return detail.elt;
  }
  if (detail.target instanceof HTMLElement) {
    return detail.target;
  }
  return null;
}

function readProgressPreference(node: HTMLElement | null): string {
  if (!(node instanceof HTMLElement)) {
    return "";
  }
  return String(node.getAttribute(PROGRESS_SKIP_ATTR) || "").trim().toLowerCase();
}

function isOptedOut(node: HTMLElement | null): boolean {
  if (!(node instanceof HTMLElement)) {
    return false;
  }
  const explicit = node.closest(`[${PROGRESS_SKIP_ATTR}]`);
  if (!(explicit instanceof HTMLElement)) {
    return false;
  }
  const value = readProgressPreference(explicit);
  return value === "false" || value === "off" || value === "none";
}

function isBackgroundPoll(node: HTMLElement | null): boolean {
  if (!(node instanceof HTMLElement)) {
    return false;
  }
  if (node.closest("[data-wm-poll]")) {
    return true;
  }
  const triggerNode = node.closest("[hx-trigger], [data-hx-trigger]");
  if (!(triggerNode instanceof HTMLElement)) {
    return false;
  }
  const trigger = String(
    triggerNode.getAttribute("hx-trigger") || triggerNode.getAttribute("data-hx-trigger") || ""
  ).trim().toLowerCase();
  return trigger.startsWith("every ");
}

function requestConfigFlag(detail: HTMXProgressDetail | null, key: string): boolean {
  if (!detail?.requestConfig || typeof detail.requestConfig !== "object") {
    return false;
  }
  return detail.requestConfig[key] === true;
}

function shouldTrackHTMXRequest(detail: unknown): boolean {
  const request = detail && typeof detail === "object" ? (detail as HTMXProgressDetail) : null;
  const source = requestSource(request);
  if (!(source instanceof HTMLElement)) {
    // Programmatic htmx.ajax() calls are usually background refreshes. They should
    // stay silent unless the caller explicitly opts into visible progress.
    return requestConfigFlag(request, "trackProgress");
  }
  if (isOptedOut(source)) {
    return false;
  }
  if (isBackgroundPoll(source)) {
    return false;
  }
  return true;
}

function requestID(detail: HTMXProgressDetail | null): string {
  if (!detail) {
    return "";
  }

  const xhrID = getProgressID(detail.xhr);
  if (xhrID) {
    return xhrID;
  }

  const configID = getProgressID(detail.requestConfig);
  if (configID) {
    return configID;
  }

  if (detail.xhr || detail.requestConfig) {
    fallbackSequence += 1;
    const nextID = `req-${fallbackSequence}`;
    setProgressID(detail.xhr, nextID);
    setProgressID(detail.requestConfig, nextID);
    return nextID;
  }

  return "";
}

function scheduleStart(): void {
  if (visible || startTimer) {
    return;
  }
  startTimer = window.setTimeout(() => {
    startTimer = 0;
    if (pendingCount <= 0 || visible) {
      return;
    }
    visible = true;
    NProgress.start();
  }, START_DELAY_MS);
}

function clearStartTimer(): void {
  if (!startTimer) {
    return;
  }
  window.clearTimeout(startTimer);
  startTimer = 0;
}

function beginTrackedRequest(id: string): void {
  if (!id || trackedRequests.has(id)) {
    return;
  }
  trackedRequests.add(id);
  pendingCount += 1;
  scheduleStart();
}

function finishTrackedRequest(id: string): void {
  if (!id || !trackedRequests.has(id)) {
    return;
  }
  trackedRequests.delete(id);
  pendingCount = Math.max(0, pendingCount - 1);
  if (pendingCount > 0) {
    return;
  }
  clearStartTimer();
  if (visible) {
    visible = false;
    NProgress.done(true);
  }
}

function startManual(): void {
  fallbackSequence += 1;
  beginTrackedRequest(`manual-${fallbackSequence}`);
}

function doneManual(): void {
  const manualIDs = Array.from(trackedRequests).filter((id) => id.startsWith("manual-"));
  manualIDs.forEach((id) => finishTrackedRequest(id));
}

function track<T>(promise: Promise<T>): Promise<T> {
  fallbackSequence += 1;
  const id = `manual-${fallbackSequence}`;
  beginTrackedRequest(id);
  return promise.finally(() => {
    finishTrackedRequest(id);
  });
}

function bindHTMXProgress(): void {
  if (!(doc.body instanceof HTMLElement) || bootedListeners) {
    return;
  }
  bootedListeners = true;

  doc.body.addEventListener("htmx:beforeRequest", (event) => {
    const detail = (event as CustomEvent<ProgressCarrier>).detail || null;
    if (!shouldTrackHTMXRequest(detail)) {
      return;
    }
    beginTrackedRequest(requestID(detail));
  });

  const complete = (event: Event) => {
    const detail = (event as CustomEvent<ProgressCarrier>).detail || null;
    finishTrackedRequest(requestID(detail));
  };

  doc.body.addEventListener("htmx:afterRequest", complete);
  doc.body.addEventListener("htmx:responseError", complete);
  doc.body.addEventListener("htmx:sendError", complete);
  doc.body.addEventListener("htmx:sendAbort", complete);
};

function configure(): void {
  NProgress.configure({
    minimum: 0.08,
    showSpinner: false,
    easing: "ease",
    speed: 260,
    trickleSpeed: 160,
  });
}

function boot(): void {
  configure();
  bindHTMXProgress();
}

if (!window.__WMU_NETWORK_PROGRESS_BOOTED) {
  window.__WMU_NETWORK_PROGRESS_BOOTED = true;
  window.WMUNetworkProgress = {
    boot,
    start: startManual,
    done: doneManual,
    track,
    shouldTrackHTMXRequest,
  };
}

boot();
